import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Sparkles, ArrowRight, RefreshCw, AlertCircle } from 'lucide-react';
import { Exercise, LearningMode, ProgrammingLanguage } from '@unity-learning/shared';
import { createMiniCours, getExercise } from '../services/api';
import { useUser } from '../hooks/useUser';
import { useLearningMode } from '../contexts/LearningModeContext';
import MiniCoursContent from '../components/MiniCoursContent';
import { trackEvent } from '../utils/analytics';
import { formatExerciseTitle } from '../utils/formatExerciseTitle';
import { buildMiniCoursBasePrompt, buildMiniCoursSimplifyPrompt } from '../utils/miniCoursPrompts';

// B1 — « Mini-cours d'abord, quiz ensuite ».
// Cette page est le point d'arrivée du flux lancé depuis le bouton 📖 des nœuds du parcours
// (LearningPath → handleMiniCoursFirst → navigate /mini-cours/:exerciseId).
// Elle :
//   1. charge l'exercice (déjà généré par HomePage.handleMiniCoursFirst) ;
//   2. POST /api/exercises/mini-cours niveau 0 (ou sert le cache DB) ;
//   3. rend le markdown via le composant partagé MiniCoursContent ;
//   4. propose « Plus simple » (re-POST niveau+1) et le CTA « Passer à l'exercice → ».
// Les templates de prompt sont COPIÉS d'ExercisePage.tsx (~lignes 1040-1067) pour rester
// identiques au mini-cours de la page d'exercice (synergie chantier 2 : même cache DB par
// (exercice, niveau, langue)). On ne dépend d'AUCUN nouvel endpoint backend.

const MAX_LEVELS = 10;

interface MiniCoursEntry {
  level: number;
  explanation: string;
  fromCache: boolean;
}

const MiniCoursFirstPage: React.FC = () => {
  const { exerciseId = '' } = useParams<{ exerciseId: string }>();
  const [searchParams] = useSearchParams();
  // C2 — nœud du parcours transmis par HomePage.handleMiniCoursFirst. Peut être absent si
  // la page est ouverte hors contexte de parcours ; dans ce cas le parcours n'avance pas
  // (comportement attendu), mais tentative + maîtrise par concept restent enregistrées.
  const learningNodeId = searchParams.get('learningNodeId');
  const knowledgePointIds = searchParams.get('knowledgePointIds');
  const navigate = useNavigate();
  const { user } = useUser();
  const { learningMode, programmingLanguage } = useLearningMode();

  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [loadingExercise, setLoadingExercise] = useState(true);
  const [entries, setEntries] = useState<MiniCoursEntry[]>([]);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 1. Charger l'exercice (généré par HomePage avant la navigation).
  useEffect(() => {
    if (!exerciseId) return;
    let cancelled = false;

    const loadExercise = async () => {
      try {
        const loaded = await getExercise(exerciseId);
        if (!cancelled && loaded) {
          setExercise(loaded);
        } else if (!cancelled) {
          setError('Exercice introuvable.');
        }
      } catch (e) {
        if (!cancelled) setError('Impossible de charger l\'exercice.');
      } finally {
        if (!cancelled) setLoadingExercise(false);
      }
    };

    loadExercise();
    return () => { cancelled = true; };
  }, [exerciseId]);

  // Label du mode pour le prompt — réplique exacte de la logique ExercisePage.tsx ~1050.
  const modeLabel =
    learningMode === LearningMode.GODOT
      ? 'Godot/GDScript'
      : learningMode === LearningMode.PYTHON_JS
        ? (programmingLanguage === ProgrammingLanguage.PYTHON ? 'Python' : 'JavaScript')
        : 'Unity/C#';

  // 2. Template niveau intermédiaire (niveau 0) — réutilise le helper partagé.
  const buildBasePrompt = useCallback((): string => {
    if (!exercise) return '';
    return buildMiniCoursBasePrompt({
      title: exercise.title,
      description: exercise.description,
      concepts: exercise.concepts,
      modeLabel,
    });
  }, [exercise, modeLabel]);

  // Template de simplification (niveau ≥ 1) — réutilise le helper partagé.
  const buildSimplifyPrompt = useCallback((previousExplanation: string, level: number): string => {
    return buildMiniCoursSimplifyPrompt(previousExplanation, level);
  }, []);

  // 3. Générer / charger un mini-cours pour un niveau donné.
  const generateMiniCours = useCallback(async (level: number) => {
    if (!user || !exercise) return;

    setIsGenerating(true);
    setError(null);
    try {
      const prompt = level === 0
        ? buildBasePrompt()
        : buildSimplifyPrompt(entries[entries.length - 1]?.explanation || '', level);

      const result = await createMiniCours({
        userId: user.id,
        exerciseId: exercise.id,
        simplificationLevel: level,
        prompt,
      });
      if (!result) throw new Error('Mini-cours indisponible.');
      // On ne remplace l'entrée que si ce niveau n'est pas déjà affiché (cache).
      setEntries(prev => {
        const existing = prev.find(e => e.level === level);
        if (existing) return prev; // déjà là, rien à faire
        return [...prev, { level, explanation: result.explanation, fromCache: Boolean(result.fromCache) }];
      });
      setCurrentLevel(level);
    } catch (e) {
      // Dégradation gracieuse : on garde le CTA vers l'exercice même si le mini-cours échoue.
      setError((e as Error).message);
    } finally {
      setIsGenerating(false);
    }
  }, [user, exercise, entries, buildBasePrompt, buildSimplifyPrompt]);

  // 4. Au premier chargement de l'exercice, lancer la génération niveau 0.
  //    C3 — `user` DOIT être dans les deps : au rechargement direct (F5/favori/lien), `user`
  //    est chargé de façon asynchrone et peut arriver APRÈS `exercise`. generateMiniCours fait
  //    `if (!user || !exercise) return` : sans `user` en dépendance, la génération était
  //    abandonnée sans jamais réessayer → page blanche (ni skeleton ni erreur). Les gardes
  //    `entries.length === 0 && !isGenerating` empêchent toute double génération.
  useEffect(() => {
    if (exercise && user && entries.length === 0 && !isGenerating) {
      generateMiniCours(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercise, user]);

  const handleSimplify = () => {
    if (currentLevel < MAX_LEVELS - 1 && !isGenerating) {
      generateMiniCours(currentLevel + 1);
    }
  };

  // Suivi : on compte l'ouverture du mini-cours (comme le fait l'accordéon ExercisePage).
  useEffect(() => {
    if (exercise) trackEvent('mini_course_opened', { exerciseId: exercise.id, source: 'first' });
  }, [exercise]);

  const goToExercise = () => {
    // C2 — réinjecter le nœud du parcours pour qu'ExercisePage le renvoie au submit
    // (backend updateLearningPathProgress). Sinon le parcours n'avance jamais via ce flux.
    // Même chose pour le point visé, qui crédite la progression par point.
    const params = new URLSearchParams();
    if (learningNodeId) params.set('learningNodeId', learningNodeId);
    if (knowledgePointIds) params.set('knowledgePointIds', knowledgePointIds);
    const qs = params.toString() ? `?${params.toString()}` : '';
    navigate(`/exercise/${exerciseId}${qs}`);
  };

  const goHome = () => {
    navigate('/');
  };

  // --- Rendu ---

  if (loadingExercise) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  const currentEntry = entries.find(e => e.level === currentLevel);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      {/* Retour */}
      <button
        onClick={goHome}
        className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-1.5" />
        Retour à l'accueil
      </button>

      {/* En-tête glass (idiome ComebackBanner.tsx : glass dark:glass-dark) */}
      <div className="glass dark:glass-dark rounded-xl p-6 mb-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl -mr-24 -mt-24 pointer-events-none"></div>
        <div className="relative">
          <div className="flex items-center mb-2">
            <BookOpen className="w-5 h-5 mr-2 text-indigo-600 dark:text-indigo-400 shrink-0" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Mini-cours d'abord
            </h1>
          </div>
          {exercise && (
            <>
              <p className="text-sm text-gray-600 dark:text-slate-300 mb-1">
                Comprenez la théorie, puis passez à la pratique.
              </p>
              <p className="text-base font-medium text-gray-900 dark:text-white">
                {formatExerciseTitle(exercise.title)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {modeLabel}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Erreur non fatale : on garde le CTA vers l'exercice (dégradation gracieuse) */}
      {error && entries.length === 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6 flex items-start">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mr-2 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
              Le mini-cours n'a pas pu être généré pour le moment.
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              Vous pouvez réessayer ou passer directement à l'exercice.
            </p>
          </div>
        </div>
      )}

      {/* Contenu du mini-cours ou skeleton de chargement */}
      {isGenerating && !currentEntry ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 mb-6">
          <div className="flex items-center mb-4">
            <RefreshCw className="w-4 h-4 text-indigo-500 animate-spin mr-2" />
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Génération du mini-cours…
            </span>
          </div>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/6"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
          </div>
        </div>
      ) : currentEntry ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 mb-4">
          {currentEntry.fromCache && (
            <div className="mb-3 text-xs text-gray-400 dark:text-gray-500 flex items-center">
              <Sparkles className="w-3 h-3 mr-1" />
              Mini-cours déjà généré précédemment (cache)
            </div>
          )}
          <MiniCoursContent markdown={currentEntry.explanation} />
        </div>
      ) : null}

      {/* « Plus simple » */}
      {currentEntry && currentLevel < MAX_LEVELS - 1 && (
        <div className="flex justify-center mb-6">
          <button
            onClick={handleSimplify}
            disabled={isGenerating}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 text-sm font-medium hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${isGenerating ? 'animate-spin' : ''}`} />
            {isGenerating ? 'Simplification…' : `Plus simple (niveau ${currentLevel + 1})`}
          </button>
        </div>
      )}

      {/* Niveaux précédents (récapitulatif replié vers le haut) */}
      {entries.length > 1 && (
        <div className="mb-6 space-y-2">
          <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">
            Niveaux précédents
          </p>
          {entries.filter(e => e.level !== currentLevel).map(e => (
            <details key={e.level} className="bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700">
              <summary className="cursor-pointer px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                Niveau {e.level} {e.level === 0 ? '(intermédiaire)' : ''}
              </summary>
              <div className="px-4 pb-3">
                <MiniCoursContent markdown={e.explanation} />
              </div>
            </details>
          ))}
        </div>
      )}

      {/* CTA principal : « Passer à l'exercice → » — TOUJOURS visible (dégradation gracieuse) */}
      <div className="sticky bottom-4 z-10">
        <button
          onClick={goToExercise}
          className="w-full inline-flex items-center justify-center px-6 py-3.5 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold shadow-lg transition-all"
        >
          Passer à l'exercice
          <ArrowRight className="w-5 h-5 ml-2" />
        </button>
      </div>
    </div>
  );
};

export default MiniCoursFirstPage;
